import { useCallback, useState } from 'react';
import {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useReactFlow,
  addEdge,
} from '@xyflow/react';
import type { Node, Connection } from '@xyflow/react';
import { Plus } from 'lucide-react';
import { randomUUID } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { WorkflowBlocksSidebar, DRAG_TYPE } from './WorkflowBlocksSidebar';
import { WorkflowCanvas } from './WorkflowCanvas';
import { WorkflowDesignerTopBar } from './WorkflowDesignerTopBar';
import { WorkflowPropertiesDialog } from './WorkflowPropertiesDialog';
import { WorkflowTestResults } from './WorkflowTestResults';
import { WorkflowTestDataLoaderDialog } from './WorkflowTestDataLoaderDialog';
import { WorkflowRunHistory } from './WorkflowRunHistory';
import { WorkflowNodeContextProvider } from './WorkflowNodeContext';
import {
  useWorkflow,
  useWorkflowGraph,
  useSaveWorkflowGraph,
  useUpdateWorkflow,
  useUpdateWorkflowStatus,
  useTestWorkflow,
  useExecuteWorkflow,
} from '@/hooks/useWorkflows';
import type { ApiWorkflowRun } from '@/api/workflows';
import { blockToNodeData } from './block-config';
import type { WorkflowBlock, WorkflowMeta, WorkflowNodeData, WorkflowStatus } from './types';
import type { WorkflowNode, WorkflowEdge } from '@pm-next/shared-types';

/** Map React Flow nodes/edges to API shape so we don't send extra props (avoids 400 with forbidNonWhitelisted). */
function toGraphPayload(
  nodes: Node[],
  edges: {
    id: string;
    source: string;
    target: string;
    sourceHandle?: string;
    targetHandle?: string;
  }[]
): { nodes: WorkflowNode[]; edges: WorkflowEdge[] } {
  return {
    nodes: nodes.map((n) => ({
      id: n.id,
      type: n.type as WorkflowNode['type'],
      position: n.position,
      data: {
        type: ((n.data?.type as string) ?? n.type) as WorkflowNode['data']['type'],
        label: (n.data?.label as string) ?? '',
        description: n.data?.description as string | undefined,
        config: (n.data?.config as Record<string, unknown>) ?? {},
      },
    })),
    edges: edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      ...(e.sourceHandle != null && { sourceHandle: e.sourceHandle }),
      ...(e.targetHandle != null && { targetHandle: e.targetHandle }),
    })),
  };
}

/** Derive the workflow-level trigger config from the trigger node so scheduled/event settings persist. */
function deriveTriggerConfig(
  nodes: { data?: { type?: string; config?: Record<string, unknown> } }[]
): Record<string, unknown> | undefined {
  const trigger = nodes.find((n) => n.data?.type === 'trigger');
  const config = trigger?.data?.config;
  if (!config || typeof config !== 'object') return undefined;
  const result: Record<string, unknown> = {};
  if (typeof config.triggerType === 'string') result.triggerType = config.triggerType;
  if (typeof config.cron === 'string' && config.cron.trim()) result.cron = config.cron;
  if (config.processingMode === 'batch' || config.processingMode === 'per-event') {
    result.processingMode = config.processingMode;
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

export interface WorkflowDesignerProps {
  workflowId?: string | null;
  templateId?: string | null;
  onBack?: () => void;
}

function WorkflowDesignerContent({
  workflowId,
  initialNodes,
  initialEdges,
  initialMeta,
  onBack,
}: {
  workflowId: string;
  initialNodes: Node[];
  initialEdges: { id: string; source: string; target: string }[];
  initialMeta: WorkflowMeta;
  onBack?: () => void;
}) {
  const { toast } = useToast();
  const { screenToFlowPosition } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const propertiesNode =
    selectedNodeId != null ? (nodes.find((n) => n.id === selectedNodeId) ?? null) : null;
  const [workflow, setWorkflow] = useState<WorkflowMeta>(initialMeta);
  const [testResult, setTestResult] = useState<ApiWorkflowRun | null>(null);
  const [testDataDialogOpen, setTestDataDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const saveGraphMutation = useSaveWorkflowGraph();
  const updateWorkflowMutation = useUpdateWorkflow();
  const updateStatusMutation = useUpdateWorkflowStatus();
  const testWorkflowMutation = useTestWorkflow(workflowId);
  const executeWorkflowMutation = useExecuteWorkflow(workflowId);

  const openProperties = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
  }, []);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData(DRAG_TYPE);
      if (!raw) return;
      try {
        const block = JSON.parse(raw) as WorkflowBlock;
        const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
        const id = randomUUID();
        const newNode: Node = {
          id,
          type: block.type,
          position,
          data: blockToNodeData(block),
        };
        setNodes((nds) => [...nds, newNode]);
      } catch {
        // ignore invalid drag data
      }
    },
    [screenToFlowPosition, setNodes]
  );

  const onUpdateNode = useCallback(
    (nodeId: string, updates: Partial<WorkflowNodeData>) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n))
      );
    },
    [setNodes]
  );

  const onDeleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      if (selectedNodeId === nodeId) setSelectedNodeId(null);
    },
    [setNodes, setEdges, selectedNodeId]
  );

  const handleSave = useCallback(async () => {
    try {
      await updateWorkflowMutation.mutateAsync({
        id: workflowId,
        data: { name: workflow.name, description: workflow.description },
      });
      const payload = toGraphPayload(nodes, edges);
      const triggerConfig = deriveTriggerConfig(payload.nodes);
      const saved = await saveGraphMutation.mutateAsync({
        id: workflowId,
        data: { ...payload, ...(triggerConfig ? { triggerConfig } : {}) },
      });
      setWorkflow((w) => ({ ...w, version: saved.version }));
      toast({ title: 'Workflow saved', description: 'Your changes have been saved.' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to save',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }, [
    workflowId,
    workflow.name,
    workflow.description,
    nodes,
    edges,
    updateWorkflowMutation,
    saveGraphMutation,
    toast,
  ]);

  const handleStatusChange = useCallback(
    async (status: WorkflowStatus) => {
      try {
        await updateStatusMutation.mutateAsync({ id: workflowId, status });
        setWorkflow((w) => ({ ...w, status }));
        toast({ title: 'Status updated', description: `Workflow is now ${status}.` });
      } catch (err) {
        toast({
          variant: 'destructive',
          title: 'Failed to update status',
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    },
    [workflowId, updateStatusMutation, toast]
  );

  const handleRun = useCallback(async () => {
    try {
      const result = await executeWorkflowMutation.mutateAsync(undefined);
      setTestResult(result);
      toast({ title: 'Workflow executed', description: `Status: ${result.status}` });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Run failed',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }, [executeWorkflowMutation, toast]);

  const handleOpenTestDataDialog = useCallback(() => {
    setTestDataDialogOpen(true);
  }, []);

  const handleRunTestWithData = useCallback(
    async (triggerData: Record<string, unknown>) => {
      try {
        const result = await testWorkflowMutation.mutateAsync(triggerData);
        setTestResult(result);
        toast({ title: 'Test run completed', description: `Status: ${result.status}` });
      } catch (err) {
        toast({
          variant: 'destructive',
          title: 'Test run failed',
          description: err instanceof Error ? err.message : 'Unknown error',
        });
        throw err;
      }
    },
    [testWorkflowMutation, toast]
  );

  return (
    <WorkflowNodeContextProvider value={{ openProperties }}>
      <div className="relative flex h-full flex-col">
        <WorkflowDesignerTopBar
          workflow={workflow}
          onWorkflowChange={(updates) => setWorkflow((w) => ({ ...w, ...updates }))}
          onBack={onBack}
          onSave={handleSave}
          onStatusChange={handleStatusChange}
          onRun={handleRun}
          onTest={handleOpenTestDataDialog}
          onOpenHistory={() => setHistoryOpen(true)}
        />
        <div className="relative min-h-0 flex-1">
          <WorkflowCanvas
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onSelectionChange={(node) => setSelectedNodeId(node?.id ?? null)}
            className="h-full w-full"
          />
          <Button
            variant="default"
            size="icon"
            className="absolute left-3 top-3 z-10 h-9 w-9 shadow-md"
            onClick={() => setSidebarOpen(true)}
            aria-label="Add step – open blocks panel"
          >
            <Plus className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Blocks sidebar overlay – no backdrop so drag-and-drop to canvas works */}
      {sidebarOpen && (
        <div
          className="fixed left-0 top-0 z-50 flex h-full w-[280px] flex-col border-r bg-background shadow-xl animate-in slide-in-from-left-2 duration-200"
          role="dialog"
          aria-label="Workflow blocks"
        >
          <WorkflowBlocksSidebar
            className="min-h-0 flex-1 flex flex-col overflow-hidden"
            onClose={() => setSidebarOpen(false)}
          />
        </div>
      )}
      <WorkflowPropertiesDialog
        selectedNode={propertiesNode}
        onUpdateNode={onUpdateNode}
        onDeleteNode={onDeleteNode}
        onClose={() => setSelectedNodeId(null)}
      />
      <WorkflowTestDataLoaderDialog
        open={testDataDialogOpen}
        onClose={() => setTestDataDialogOpen(false)}
        onRunTest={handleRunTestWithData}
      />
      <WorkflowTestResults
        run={testResult}
        open={!!testResult}
        onClose={() => setTestResult(null)}
      />
      <WorkflowRunHistory
        workflowId={workflowId}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </WorkflowNodeContextProvider>
  );
}

export function WorkflowDesigner({
  workflowId: workflowIdProp,
  templateId: _templateId,
  onBack,
}: WorkflowDesignerProps = {}) {
  const workflowId = workflowIdProp ?? null;
  const { data: workflowData, isLoading: workflowLoading } = useWorkflow(workflowId);
  const { data: graphData, isLoading: graphLoading } = useWorkflowGraph(workflowId);

  const isLoading = workflowLoading || graphLoading;
  const loaded = workflowId && workflowData && graphData;

  if (!workflowId) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        No workflow selected.
      </div>
    );
  }

  if (isLoading || !loaded) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Loading workflow…
      </div>
    );
  }

  const initialNodes = (graphData.nodes ?? []).map((n) => ({
    id: n.id,
    type: n.type,
    position: n.position,
    data: n.data as unknown as Record<string, unknown>,
  })) as Node[];
  const initialEdges = graphData.edges ?? [];
  const validStatuses: WorkflowStatus[] = ['draft', 'active', 'inactive', 'archived'];
  const initialMeta: WorkflowMeta = {
    id: workflowData.id,
    name: workflowData.name,
    description: workflowData.description ?? '',
    status: (validStatuses.includes(workflowData.status as WorkflowStatus)
      ? workflowData.status
      : 'draft') as WorkflowStatus,
    version: workflowData.version,
  };

  return (
    <ReactFlowProvider>
      <WorkflowDesignerContent
        key={workflowId}
        workflowId={workflowId}
        initialNodes={initialNodes}
        initialEdges={initialEdges}
        initialMeta={initialMeta}
        onBack={onBack}
      />
    </ReactFlowProvider>
  );
}
